/**
 * InstagramPanel — pestaña Instagram de Redes Orgánicas.
 *
 * Datos: /api/meta/organic/instagram (Instagram Graph API oficial, no scraping).
 *
 * ORGÁNICO DE VERDAD: las métricas de cuenta de IG incluyen anuncios (en
 * Cantabria >99% del alcance era pauta). El backend separa la pauta con
 * breakdown=media_product_type; aquí los KPIs usan `instagram.organic` y la
 * pauta solo aparece en un aviso de una línea. El alcance NO tiene versión
 * orgánica (son personas deduplicadas: no se puede restar); por eso solo se
 * muestra por formato.
 *
 * Historias y menciones llegan por endpoints aparte (tardan más): se piden al
 * montar y se guardan en memoria por periodo para no repetir al cambiar de red.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import {
  FaInstagram, FaUsers, FaEye, FaHeart, FaComment, FaExclamationTriangle,
  FaChartBar, FaTrophy, FaVenusMars, FaMapMarkerAlt, FaUserFriends,
  FaBookmark, FaShare, FaUserPlus, FaLayerGroup, FaBirthdayCake, FaAt,
  FaCircleNotch, FaRegClock, FaMousePointer, FaPercent,
} from 'react-icons/fa';
import { metaApi } from '../../services/metaService';
import {
  numFmt, compact, pct1, pct2, filterByRange, insightWindowLabel, Kpi, Hint, longDate,
  shortText, getSocialLocale, resolveRange,
} from '../../components/SocialApi/socialApiShared';
import {
  PeriodSummary, PostsSection, PostCard, PostsTable, PostModal, CommentsBlock,
  StackedByDateChart, TopChart, chartBox, useDayPick, DailyChart, BarList,
  MetricGrid, SectionHead, PaidNote, engagementRate, useLazy, MonthlyFollowersCard,
} from './organicShared';

const IG_INSIGHTS_MAX_DAYS = 30;

const TYPE_META = {
  IMAGE:          { key: 'typeImage' },
  VIDEO:          { key: 'typeVideo' },
  CAROUSEL_ALBUM: { key: 'typeCarousel' },
  REELS:          { key: 'typeReel' },
};
const postTypeKey = (p) => (p.product_type === 'REELS' ? 'REELS' : p.type);
// Interacciones de una publicación según Meta (me gusta + comentarios +
// guardados + compartidos). Si no llegó el insight, al menos me gusta + comentarios.
const inter = (p) => p.total_interactions ?? ((p.likes || 0) + (p.comments || 0));

const FORMAT_COLORS = { REEL: '#DD2A7B', CAROUSEL: '#8b5cf6', POST: '#073C5C', STORY: '#F68D2E' };
const GENDER_COLORS = { F: '#ec4899', M: '#3b82f6', U: '#94a3b8' };

// Código de país → nombre en el idioma de la app ("MX" → "México").
function countryName(code) {
  if (!/^[A-Z]{2}$/.test(code || '')) return code;
  try {
    return new Intl.DisplayNames([getSocialLocale()], { type: 'region' }).of(code) || code;
  } catch (e) {
    return code;
  }
}
// "Mexico City, Distrito Federal" → "Mexico City"
const cityName = (c) => (c || '').split(',')[0];

export default function InstagramPanel({ data, range, onRangeChange, username, t }) {
  const [modalPost, setModalPost] = useState(null);
  const typeLabel = useCallback((p) => {
    const m = TYPE_META[postTypeKey(p)];
    return t(`organic.${m ? m.key : 'typeOther'}`);
  }, [t]);

  const ig = data?.instagram || {};
  const organic = ig.organic || {};
  const paid = ig.paid || {};
  const eng = ig.engagement || {};
  const follows = ig.follows;
  const insightSub = insightWindowLabel(range, IG_INSIGHTS_MAX_DAYS, t);

  const posts = useMemo(
    () => filterByRange(data?.posts || [], range).map((p) => ({
      ...p, likes: p.likes || 0, comments: p.comments || 0, saves: p.saves || 0, shares: p.shares || 0, inter: inter(p),
    })),
    [data, range]);
  const dayPick = useDayPick(posts, setModalPost);
  const best = useMemo(() => posts.reduce((b, p) => (!b || p.inter > b.inter ? p : b), null), [posts]);
  const sum = (k) => posts.reduce((a, p) => a + (p[k] || 0), 0);

  const technicalError = ig.insights_error || data?.media_error;

  const sorts = useMemo(() => [
    { id: 'recent', label: t('organic.sortRecent'), fn: (a, b) => new Date(b.timestamp) - new Date(a.timestamp) },
    { id: 'top', label: t('organic.sortTop'), fn: (a, b) => b.inter - a.inter },
  ], [t]);

  const cardMetrics = (p) => [
    { icon: <FaHeart />, value: numFmt.format(p.likes), title: t('organic.likes') },
    { icon: <FaComment />, value: numFmt.format(p.comments), title: t('organic.comments') },
    { icon: <FaBookmark />, value: numFmt.format(p.saves), title: t('organic.statSaves') },
  ];

  return (
    <>
      <section className="soc-kpis">
        <Kpi icon={<FaUsers />} accent="#DD2A7B" label={t('organic.followers')} value={compact(ig.followers)}
             sub={follows ? t('organic.netInPeriod', { n: `${follows.net > 0 ? '+' : ''}${numFmt.format(follows.net)}` }) : null}
             hint={follows
               ? `${t('organic.followersHint')} ${t('organic.gainedLost', { gained: numFmt.format(follows.follows), lost: numFmt.format(follows.unfollows) })}`
               : t('organic.followersHint')} />
        <Kpi icon={<FaEye />} accent="#3b82f6" label={t('organic.organicViews')} value={compact(organic.views ?? ig.views)} sub={insightSub} hint={t('organic.organicViewsHint')} />
        <Kpi icon={<FaHeart />} accent="#F68D2E" label={t('organic.interactions')} value={compact(organic.total_interactions ?? eng.total_interactions)} sub={insightSub} hint={t('organic.organicInteractionsHint')} />
        <Kpi icon={<FaPercent />} accent="#8b5cf6" label={t('organic.engagement')} value={pct2(engagementRate(posts, ig.followers))}
             sub={posts.length ? t('organic.engagementSub', { n: numFmt.format(posts.length) }) : t('organic.engagementNone')}
             hint={t('organic.engagementHint')} />
        <Kpi icon={<FaUserFriends />} accent="#10b981" label={t('organic.profileVisits')} value={compact(ig.profile_views)} sub={insightSub} hint={t('organic.profileVisitsHintIg')} />
      </section>

      {paid.views > 0 && <PaidNote t={t} n={compact(paid.views)} />}

      {technicalError && (
        <div className="soc-warn" title={technicalError}><FaExclamationTriangle /> {t('organic.partialData')}</div>
      )}

      <PeriodSummary
        t={t}
        stats={[
          { label: t('organic.statPosts'), value: numFmt.format(posts.length) },
          { label: t('organic.statSaves'), value: numFmt.format(sum('saves')), hint: t('organic.statSavesHint') },
          { label: t('organic.statShares'), value: numFmt.format(sum('shares')) },
          { label: t('organic.statWebClicks'), value: eng.website_clicks != null ? numFmt.format(eng.website_clicks) : '—' },
        ]}
        best={best && best.inter > 0 ? best : null}
        bestTitle={t('organic.bestPost')}
        bestMetric={t('organic.nInteractions', { n: numFmt.format(best?.inter || 0) })}
        placeholderIcon={<FaInstagram />}
        onOpen={setModalPost}
      />

      <div className="soc-grid">
        <DailyFollowersCard data={data} t={t} />
        <FormatsCard formats={ig.formats} t={t} />
      </div>

      <div className="soc-grid">
        <MonthlyFollowersCard username={username} network="instagram" color="#DD2A7B" t={t} />
      </div>

      {posts.length > 0 && (
        <div className="soc-grid">
          <section className="soc-chart-card col-8">
            <div className="soc-chart-head">
              <span className="soc-chart-title"><FaChartBar /> {t('organic.chartInterByPost')}</span>
              <span className="soc-chart-tag">{t('organic.byDate')}</span>
            </div>
            {chartBox(<StackedByDateChart items={posts} captionOf={(p) => p.caption} onPick={dayPick.pick} series={[
              { key: 'likes', label: t('organic.likes'), color: '#F68D2E' },
              { key: 'comments', label: t('organic.comments'), color: '#073C5C' },
              { key: 'saves', label: t('organic.statSaves'), color: '#8b5cf6' },
              { key: 'shares', label: t('organic.shares'), color: '#10b981' },
            ]} />)}
          </section>
          <section className="soc-chart-card col-4">
            <div className="soc-chart-head"><span className="soc-chart-title"><FaTrophy /> {t('organic.chartTopPosts')}</span></div>
            {chartBox(<TopChart items={posts} t={t} valueOf={(p) => p.inter} captionOf={(p) => p.caption} color="#DD2A7B" onOpen={setModalPost}
              tooltipLabel={(p) => t('organic.nInteractions', { n: numFmt.format(p.inter) })} />)}
          </section>
        </div>
      )}

      <AudienceSection demo={data?.demographics} t={t} />

      <StoriesSection username={username} range={range} t={t} />

      <MentionsSection username={username} t={t} />

      <PostsSection
        t={t}
        title={t('organic.postsTitle')}
        icon={<FaInstagram />}
        items={dayPick.items}
        day={dayPick.day}
        onClearDay={dayPick.clear}
        sectionRef={dayPick.sectionRef}
        sorts={sorts}
        emptyText={t('organic.emptyPosts')}
        range={range}
        onRangeChange={onRangeChange}
        renderGrid={(list) => (
          <div className="soc-posts">
            {list.map((p) => (
              <PostCard key={p.id} post={p} t={t} onOpen={setModalPost} badge={typeLabel(p)}
                metrics={cardMetrics(p)} placeholderIcon={<FaInstagram />} />
            ))}
          </div>
        )}
        renderTable={(list) => (
          <PostsTable posts={list} t={t} onOpen={setModalPost} firstLabel={t('organic.colPost')} columns={[
            { label: t('organic.colType'), render: (p) => typeLabel(p) },
            { label: t('organic.reach'), render: (p) => (p.reach != null ? numFmt.format(p.reach) : '—') },
            { label: t('organic.statSaves'), render: (p) => numFmt.format(p.saves) },
            { label: t('organic.shares'), render: (p) => numFmt.format(p.shares) },
            { label: t('organic.colInter'), render: (p) => numFmt.format(p.inter), strong: true },
          ]} />
        )}
      />

      {modalPost && (
        <PostModal t={t} post={modalPost} onClose={() => setModalPost(null)} network="Instagram"
          badge={typeLabel(modalPost)} metrics={[]} placeholderIcon={<FaInstagram />}
          footer={<CommentsBlock key={modalPost.id} t={t} fetchComments={
            async () => (await metaApi.organicComments(username, modalPost.id, 'instagram')).comments || []
          } />}>
          <IgPostMetrics post={modalPost} username={username} t={t} />
        </PostModal>
      )}
    </>
  );
}

function DailyFollowersCard({ data, t }) {
  const days = data?.daily_followers || [];
  const total = days.reduce((a, d) => a + (d.value || 0), 0);
  return (
    <section className="soc-chart-card col-8">
      <div className="soc-chart-head">
        <span className="soc-chart-title"><FaUserPlus /> {t('organic.dailyFollowers')}
          {data?.daily_followers_partial && <Hint text={t('organic.dailyFollowersPartial')} />}
        </span>
        {days.length > 0 && <span className="soc-chart-tag">+{numFmt.format(total)} {t('organic.newFollowersWord')}</span>}
      </div>
      {days.length === 0 ? (
        <div className="soc-empty-box">{t('organic.dailyFollowersNone')}</div>
      ) : chartBox(<DailyChart days={days.map((d) => ({ date: d.date, value: d.value }))}
          series={[{ key: 'value', label: t('organic.newFollowersPost'), color: '#DD2A7B' }]} hideLegend />)}
    </section>
  );
}

// Meta no reporta interacciones por separado para carruseles (llegan sin
// total_interactions): las cuenta dentro de "publicaciones". Mostrar
// "Carruseles: 0 interacciones" engañaría, así que se suman a publicaciones.
function mergeCarousels(formats) {
  const list = (formats || []).map((f) => ({ ...f }));
  const car = list.find((f) => f.format === 'CAROUSEL');
  const post = list.find((f) => f.format === 'POST');
  if (!car || car.total_interactions != null) return list;
  if (post) {
    post.reach = (post.reach || 0) + (car.reach || 0);
    post.views = (post.views || 0) + (car.views || 0);
    return list.filter((f) => f !== car);
  }
  return list.map((f) => (f === car ? { ...f, format: 'POST' } : f));
}

function FormatsCard({ formats, t }) {
  const rows = mergeCarousels(formats)
    .filter((f) => (f.total_interactions || f.reach))
    .sort((a, b) => (b.total_interactions || 0) - (a.total_interactions || 0));
  const max = Math.max(1, ...rows.map((f) => f.total_interactions || 0));
  return (
    <section className="soc-chart-card col-4">
      <div className="soc-chart-head">
        <span className="soc-chart-title"><FaLayerGroup /> {t('organic.formatsTitle')}<Hint text={t('organic.formatsHint')} /></span>
      </div>
      {rows.length === 0 ? (
        <div className="soc-empty-box">—</div>
      ) : (
        <ul className="org-formats">
          {rows.map((f) => (
            <li key={f.format}>
              <div className="org-format-head">
                <span className="org-format-name">{t(`organic.fmt${f.format}`)}</span>
                <span className="org-format-val">{t('organic.nInteractions', { n: compact(f.total_interactions || 0) })}</span>
              </div>
              <div className="org-bar"><span style={{ width: `${((f.total_interactions || 0) / max) * 100}%`, background: FORMAT_COLORS[f.format] }} /></div>
              {f.reach != null && <span className="org-format-sub">{t('organic.formatReach', { n: compact(f.reach) })}</span>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function AudienceSection({ demo, t }) {
  const [where, setWhere] = useState('cities');
  if (!demo || !(demo.gender?.length || demo.age?.length || demo.cities?.length)) return null;
  const gender = (demo.gender || []).map((g) => ({ ...g, label: t(`organic.gender${g.key}`) === `organic.gender${g.key}` ? g.key : t(`organic.gender${g.key}`) }));
  const placeRows = (where === 'cities' ? demo.cities : demo.countries) || [];
  return (
    <>
      <SectionHead title={t('organic.audienceTitle')} sub={t('organic.audienceSub')} />
      <div className="soc-grid">
        {gender.length > 0 && (
          <section className="soc-chart-card col-4">
            <div className="soc-chart-head"><span className="soc-chart-title"><FaVenusMars /> {t('organic.gender')}</span></div>
            <div className="soc-donut-wrap">
              <div className="soc-donut-chart">
                <Doughnut
                  data={{ labels: gender.map((g) => g.label), datasets: [{ data: gender.map((g) => g.pct), backgroundColor: gender.map((g) => GENDER_COLORS[g.key] || '#94a3b8'), borderWidth: 0 }] }}
                  options={{ plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => `${c.label}: ${c.parsed}%` } } }, cutout: '62%', maintainAspectRatio: false }}
                />
              </div>
              <div className="soc-legend">
                {gender.map((g) => (
                  <div className="soc-leg" key={g.key}>
                    <span className="soc-leg-dot" style={{ background: GENDER_COLORS[g.key] || '#94a3b8' }} />
                    <span className="soc-leg-name">{g.label}</span>
                    <span className="soc-leg-val">{g.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
        {demo.age?.length > 0 && (
          <section className="soc-chart-card col-4">
            <div className="soc-chart-head"><span className="soc-chart-title"><FaBirthdayCake /> {t('organic.age')}</span><span className="soc-chart-tag">{t('organic.ofFollowers')}</span></div>
            <BarList rows={demo.age.filter((a) => a.pct >= 0.5).map((a) => ({ label: a.key, pct: a.pct }))} color="#073C5C" />
          </section>
        )}
        {(demo.cities?.length > 0 || demo.countries?.length > 0) && (
          <section className="soc-chart-card col-4">
            <div className="soc-chart-head">
              <span className="soc-chart-title"><FaMapMarkerAlt /> {t('organic.whereTitle')}</span>
              <div className="org-seg small">
                <button type="button" className={where === 'cities' ? 'active' : ''} onClick={() => setWhere('cities')}>{t('organic.cities')}</button>
                <button type="button" className={where === 'countries' ? 'active' : ''} onClick={() => setWhere('countries')}>{t('organic.countries')}</button>
              </div>
            </div>
            <BarList rows={placeRows.slice(0, 6).map((r) => ({ label: where === 'cities' ? cityName(r.key) : countryName(r.key), pct: r.pct }))} color="#F68D2E" />
          </section>
        )}
      </div>
    </>
  );
}

function StoriesSection({ username, range, t }) {
  const { from, to } = resolveRange(range);
  const state = useLazy(`stories|${username}|${from}|${to}`,
    () => metaApi.organicInstagramStories(username, from, to));
  const stories = state.data?.stories || [];
  const refreshing = state.data?.refreshing;

  const views = stories.reduce((a, s) => a + (s.views || 0), 0);
  const exits = stories.reduce((a, s) => a + (s.exits || 0), 0);
  const withReach = stories.filter((s) => s.reach != null);
  const avgReach = withReach.length ? Math.round(withReach.reduce((a, s) => a + s.reach, 0) / withReach.length) : null;

  return (
    <>
      <SectionHead title={t('organic.stories')} sub={t('organic.storiesSub')}
        extra={(state.status === 'loading' || refreshing) && <span className="org-updating"><FaCircleNotch className="soc-spin" /> {t('organic.updating')}</span>} />
      {state.status === 'loading' ? (
        <div className="org-skel-block strip" />
      ) : stories.length === 0 ? (
        <div className="soc-card org-empty compact">
          <p className="org-empty-title">{t('organic.storiesNone')}</p>
          <p className="org-empty-hint">{t('organic.storiesNoneHint')}</p>
        </div>
      ) : (
        <section className="soc-card org-stories">
          <MetricGrid items={[
            { label: t('organic.storiesPublished'), value: numFmt.format(stories.length) },
            { label: t('organic.storiesAvgReach'), value: avgReach != null ? compact(avgReach) : '—' },
            { label: t('organic.replies'), value: numFmt.format(stories.reduce((a, s) => a + (s.replies || 0), 0)) },
            { label: t('organic.storiesExitRate'), value: views ? pct1(exits / views) : '—', hint: t('organic.storiesExitHint') },
          ]} />
          <div className="org-hscroll">
            {stories.map((s) => (
              <a key={s.id} className="org-story" href={s.permalink || undefined} target="_blank" rel="noreferrer">
                <span className="org-story-thumb">
                  {s.thumbnail ? <img src={s.thumbnail} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : null}
                  <FaInstagram className="org-story-ph" />
                </span>
                <span className="org-story-meta"><FaEye /> {compact(s.reach)}</span>
                <span className="org-story-date">{longDate(s.timestamp)}</span>
              </a>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

function MentionsSection({ username, t }) {
  const state = useLazy(`tags|${username}`, () => metaApi.organicInstagramTags(username));
  const tags = state.data?.tags || [];
  if (state.status === 'ok' && tags.length === 0) return null;
  return (
    <>
      <SectionHead title={t('organic.mentionsTitle')} sub={t('organic.mentionsSub')}
        extra={state.status === 'loading' && <span className="org-updating"><FaCircleNotch className="soc-spin" /> {t('organic.updating')}</span>} />
      {state.status === 'loading' ? (
        <div className="org-skel-block strip" />
      ) : state.status === 'error' ? (
        <div className="soc-card org-empty compact"><p className="org-empty-hint">{t('organic.mentionsError')}</p></div>
      ) : (
        <div className="org-hscroll org-mentions">
          {tags.map((m) => (
            <a key={m.id} className="org-mention" href={m.permalink || undefined} target="_blank" rel="noreferrer">
              <span className="org-mention-thumb">
                {m.thumbnail ? <img src={m.thumbnail} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : null}
                <FaAt className="org-story-ph" />
              </span>
              <span className="org-mention-body">
                <span className="org-mention-user">@{m.username}</span>
                <span className="org-mention-caption">{shortText(m.caption, 90)}</span>
                <span className="org-mention-meta"><FaHeart /> {compact(m.likes)} · {longDate(m.timestamp)}</span>
              </span>
            </a>
          ))}
        </div>
      )}
    </>
  );
}

/** Métricas de la publicación en el modal. Las extra (visitas al perfil,
 *  seguidores nuevos o tiempo visto en reels) se piden al abrirla. */
function IgPostMetrics({ post, username, t }) {
  const [extra, setExtra] = useState(null);
  const isReel = post.product_type === 'REELS';
  useEffect(() => {
    let alive = true;
    metaApi.organicMediaDetail(username, post.id, post.product_type)
      .then((r) => { if (alive) setExtra(r.detail || {}); })
      .catch(() => { if (alive) setExtra({}); });
    return () => { alive = false; };
  }, [username, post.id, post.product_type]);
  const dash = (v) => (v == null ? '—' : numFmt.format(v));
  const pending = extra === null ? '…' : null;
  return (
    <MetricGrid compact items={[
      { icon: <FaEye />, label: t('organic.reach'), value: dash(post.reach) },
      { icon: <FaChartBar />, label: t('organic.views'), value: dash(post.views) },
      { icon: <FaHeart />, label: t('organic.likes'), value: dash(post.likes) },
      { icon: <FaComment />, label: t('organic.comments'), value: dash(post.comments) },
      { icon: <FaBookmark />, label: t('organic.statSaves'), value: dash(post.saves) },
      { icon: <FaShare />, label: t('organic.shares'), value: dash(post.shares) },
      ...(isReel
        ? [{ icon: <FaRegClock />, label: t('organic.avgWatchTime'), value: pending || (extra.ig_reels_avg_watch_time != null ? `${extra.ig_reels_avg_watch_time} s` : '—') }]
        : [
          { icon: <FaMousePointer />, label: t('organic.profileVisits'), value: pending || dash(extra.profile_visits) },
          { icon: <FaUserPlus />, label: t('organic.newFollowersPost'), value: pending || dash(extra.follows) },
        ]),
    ]} />
  );
}
